import { Component, EventEmitter, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { RolesService } from '../roles.service';
import { ModulePermission, RoleDetailResponse } from '../roles.model';
import { ConfirmActionModalData } from '../../../shared/components/confirm-action-modal/confirm-action-modal.model';
import { HeaderAction } from '../../../shared/components/page-header/page-header.component';

interface PanelPermission {
  name: string;
  code: string;
  granted: boolean;
}

interface PanelModule {
  name: string;
  icon: string;
  granted: number;
  total: number;
  permissions: PanelPermission[];
}

const MODULE_DEFS = [
  {
    name: 'User Management', icon: 'assets/icons/users.svg',
    perms: [
      { name: 'View users', code: 'users.view' },
      { name: 'Create users', code: 'users.create' },
      { name: 'Edit users', code: 'users.update' },
      { name: 'Delete users', code: 'users.delete' },
      { name: 'Suspend users', code: 'users.suspend' },
    ],
  },
  {
    name: 'Roles & Permissions', icon: 'assets/icons/shield.svg',
    perms: [
      { name: 'View roles', code: 'roles.view' },
      { name: 'Create roles', code: 'roles.create' },
      { name: 'Edit roles', code: 'roles.update' },
      { name: 'Delete roles', code: 'roles.delete' },
    ],
  },
  {
    name: 'Geographic Nodes', icon: 'assets/icons/pin.svg',
    perms: [
      { name: 'View nodes', code: 'nodes.view' },
      { name: 'Create nodes', code: 'nodes.create' },
      { name: 'Edit nodes', code: 'nodes.update' },
      { name: 'Delete nodes', code: 'nodes.delete' },
    ],
  },
  {
    name: 'Channels', icon: 'assets/icons/check.svg',
    perms: [
      { name: 'View channels', code: 'channels.view' },
      { name: 'Create channels', code: 'channels.create' },
      { name: 'Edit channels', code: 'channels.update' },
      { name: 'Manage keys', code: 'channels.keys' },
    ],
  },
  {
    name: 'POS', icon: 'assets/icons/settings.svg',
    perms: [
      { name: 'View POS', code: 'pos.view' },
      { name: 'Create POS', code: 'pos.create' },
      { name: 'Edit POS', code: 'pos.update' },
      { name: 'Delete POS', code: 'pos.delete' },
    ],
  },
  {
    name: 'SKU Management', icon: 'assets/icons/sku_rules.svg',
    perms: [
      { name: 'View SKUs', code: 'skus.view' },
      { name: 'Create SKUs', code: 'skus.create' },
      { name: 'Edit SKUs', code: 'skus.update' },
      { name: 'Delete SKUs', code: 'skus.delete' },
    ],
  },
  {
    name: 'Dispatch & Operations', icon: 'assets/icons/dispatch_&_operations.svg',
    perms: [
      { name: 'View dispatch', code: 'dispatch.view' },
      { name: 'Assign tasks', code: 'dispatch.assign' },
      { name: 'Close tasks', code: 'dispatch.close' },
    ],
  },
  {
    name: 'Reports & Analytics', icon: 'assets/icons/reports_&_analytics.svg',
    perms: [
      { name: 'View reports', code: 'reports.view' },
      { name: 'Export data', code: 'reports.export' },
    ],
  },
];

const TOTAL_PERMISSIONS = MODULE_DEFS.reduce((s, m) => s + m.perms.length, 0);

@Component({
  selector: 'app-role-detail',
  standalone: false,
  templateUrl: './role-detail.component.html',
  styleUrls: ['./role-detail.component.scss'],
})
export class RoleDetailComponent implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private rolesService = inject(RolesService);
  private _subs: Subscription[] = [];

  readonly auditLogAction = new EventEmitter<void>();
  readonly createRoleAction = new EventEmitter<void>();

  roleId = '';
  role: RoleDetailResponse | null = null;
  isLoading = true;

  panelModules: PanelModule[] = [];
  openModules: { [name: string]: boolean } = {};

  isDeleteModalOpen = false;
  deleteModalData: ConfirmActionModalData | null = null;

  private permissionsMap: { [code: string]: boolean } = {};

  get pageHeaderActions(): HeaderAction[] {
    return [
      { label: 'Audit log', variant: 'ghost', emitter: this.auditLogAction },
      { label: '+ Create role', variant: 'primary', emitter: this.createRoleAction },
    ];
  }

  get headerSubtitle(): string {
    if (!this.role) return '';
    const parts: string[] = [
      `${this.role.usersCount} linked user${this.role.usersCount !== 1 ? 's' : ''}`,
      `Last edited ${new Date(this.role.lastEditedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}${this.role.lastEditedBy ? ' by ' + this.role.lastEditedBy : ''}`,
    ];
    return parts.join(' · ');
  }

  ngOnInit(): void {
    this.roleId = this.route.snapshot.paramMap.get('id') ?? '';
    this._subs.push(
      this.auditLogAction.subscribe(() => this.openAuditLog()),
      this.createRoleAction.subscribe(() => this.createRole()),
    );
    this.loadRole();
  }

  ngOnDestroy(): void {
    this._subs.forEach(s => s.unsubscribe());
  }

  // ── Data ──────────────────────────────────────────────────

  loadRole(): void {
    this.isLoading = true;
    this.rolesService.findOne(this.roleId).subscribe({
      next: (role) => {
        this.role = role;
        this.permissionsMap = this.buildPermissionsMap(role);
        this.panelModules = this.buildPanelModules(role);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private buildPermissionsMap(role: RoleDetailResponse): { [code: string]: boolean } {
    const map: { [code: string]: boolean } = {};
    role.modules?.forEach(mod => {
      const perms = mod.permissions && Object.keys(mod.permissions).length > 0
        ? mod.permissions
        : this.legacyFlagsToPermissions(mod);
      Object.assign(map, perms);
    });
    return map;
  }

  private legacyFlagsToPermissions(mod: ModulePermission): { [code: string]: boolean } {
    const r: { [code: string]: boolean } = {};
    switch (mod.name) {
      case 'User Management':
        r['users.view'] = mod.has_read; r['users.create'] = mod.has_create;
        r['users.update'] = mod.has_update; r['users.delete'] = mod.has_delete;
        r['users.suspend'] = mod.has_delete;
        break;
      case 'Roles & Permissions':
        r['roles.view'] = mod.has_read; r['roles.create'] = mod.has_create;
        r['roles.update'] = mod.has_update; r['roles.delete'] = mod.has_delete;
        break;
      case 'Geographic Nodes':
        r['nodes.view'] = mod.has_read; r['nodes.create'] = mod.has_create;
        r['nodes.update'] = mod.has_update; r['nodes.delete'] = mod.has_delete;
        break;
      case 'Channels':
        r['channels.view'] = mod.has_read;
        r['channels.create'] = mod.has_create;
        r['channels.update'] = mod.has_update;
        r['channels.keys'] = mod.has_update;
        break;
      case 'POS':
        r['pos.view'] = mod.has_read;
        r['pos.create'] = mod.has_create;
        r['pos.update'] = mod.has_update;
        r['pos.delete'] = mod.has_delete;
        break;
      case 'SKU Management':
        r['skus.view'] = mod.has_read;
        r['skus.create'] = mod.has_create;
        r['skus.update'] = mod.has_update;
        r['skus.delete'] = mod.has_delete;
        break;
      case 'Surveys':
        r['surveys.view'] = mod.has_read;
        r['surveys.create'] = mod.has_create;
        r['surveys.update'] = mod.has_update;
        r['surveys.delete'] = mod.has_delete;
        break;
      case 'Dispatch & Operations':
        r['dispatch.view'] = mod.has_read;
        r['dispatch.assign'] = mod.has_update;
        r['dispatch.close'] = mod.has_delete;
        break;
      case 'Reports & Analytics':
        r['reports.view'] = mod.has_read;
        r['reports.export'] = mod.has_update;
        break;
    }
    return r;
  }

  private buildPanelModules(role: RoleDetailResponse): PanelModule[] {
    return MODULE_DEFS.map(def => {
      const mod = role.modules?.find(m => m.name === def.name);
      const permsMap: Record<string, boolean> = mod?.permissions ?? {};
      const permissions = def.perms.map(p => ({
        name: p.name,
        code: p.code,
        granted: !!permsMap[p.code],
      }));
      return {
        name: def.name,
        icon: def.icon,
        granted: permissions.filter(p => p.granted).length,
        total: def.perms.length,
        permissions,
      };
    });
  }

  // ── Panel helpers ─────────────────────────────────────────

  toggleModule(name: string): void {
    this.openModules[name] = !this.openModules[name];
  }

  get totalGranted(): number {
    return this.panelModules.reduce((s, m) => s + m.granted, 0);
  }

  get totalPermissions(): number {
    return TOTAL_PERMISSIONS;
  }

  getProgressPercent(mod: PanelModule): number {
    return mod.total === 0 ? 0 : (mod.granted / mod.total) * 100;
  }

  get displayCode(): string {
    return this.role ? 'ROLE-' + this.role.id.substring(0, 5).toUpperCase() : '';
  }

  // ── Navigation ────────────────────────────────────────────

  goBack(): void {
    this.router.navigate(['/roles']);
  }

  editRole(): void {
    this.router.navigate(['/roles', this.roleId, 'edit']);
  }

  openAuditLog(): void {
    this.router.navigate(['/audit-log']);
  }

  createRole(): void {
    this.router.navigate(['/roles/create']);
  }

  duplicate(): void {
    this.router.navigate(['/roles/create'], {
      state: { initialPermissions: this.permissionsMap },
    });
  }

  viewAffectedUsers(): void {
    this.router.navigate(['/users'], { queryParams: { roleId: this.roleId } });
  }

  // ── Delete modal ──────────────────────────────────────────

  openDeleteModal(): void {
    if (!this.role || this.role.usersCount > 0) return;
    const { name, usersCount } = this.role;

    this.deleteModalData = {
      icon: 'trash',
      title: 'Delete role?',
      description: `You are about to permanently delete **${name}**. This action cannot be undone.`,
      effects: [
        ...(usersCount > 0
          ? [{ type: 'negative' as const, text: `${usersCount} linked user${usersCount !== 1 ? 's' : ''} will lose their role and permissions` }]
          : []),
        { type: 'negative', text: 'All permission assignments for this role will be removed' },
        { type: 'positive', text: 'Users, audit logs, and historical data are preserved' },
      ],
      confirmation: {
        label: name,
        expectedValue: name,
        placeholder: `Type "${name}" to confirm`,
      },
      actions: [
        { label: 'Delete role', style: 'danger', action: 'delete', disabledUntilConfirmed: true },
      ],
    };
    this.isDeleteModalOpen = true;
  }

  onDeleteModalAction(action: string): void {
    if (action !== 'delete') return;
    this.rolesService.delete(this.roleId).subscribe({
      next: () => {
        this.closeDeleteModal();
        this.router.navigate(['/roles']);
      },
      error: (err) => console.error('Failed to delete role:', err),
    });
  }

  closeDeleteModal(): void {
    this.isDeleteModalOpen = false;
    this.deleteModalData = null;
  }
}
